'use strict';

DataQueryExecutorFactory.$inject = [
	'mx.shell.SearchProviderService',
	'mx.SolutionBuilderAgent.Http',
	'mx.shell.ObjectMapper',
	'mx.shell.DownloadHelpService',
	'mx.sb.Agent.DataQuery.Enums',
	'mx.sb.Agent.DataQuery.FilterOperatorsFactory',
];
export default function DataQueryExecutorFactory(searchService, http, objectMapper, downloadHelpService, dataQueryEnums, filterOperatorsFactory) {
	return function DataQueryExecutor() {
		let structureNodeId;
		let entityTypes;
		let widgetId;
		let dataQueryId;
		let totalCount;
		let filters = [];
		let quickFilters = [];

		const ColumnSortingEnum = dataQueryEnums.ColumnSortingEnum;

		return {
			run: run,
			setTotalCount: setTotalCount,
			getDataCount: getDataCount,
			sortingBuild: sortingBuild,
			paginationBuild: paginationBuild,
			searchBuild: searchBuild,
			structureBuild: structureBuild,
			quickFiltersBuild: quickFiltersBuild,
			columnsBuild: columnsBuild,
			init: init,
			getTypes: getTypes,
			usePostBack: true,
			supportsGrouping: supportsGrouping,
			getGroupingTree: getGroupingTree,
			executeExportData: executeExportData,
			getIdentifier: getIdentifier,
		};

		function init(viewType, parameters) {
			structureNodeId = parameters.structureNodeId;
			entityTypes = parameters.entityTypes;
			widgetId = parameters.id;
			dataQueryId = parameters.dataQueryId;
			return [];
		}

		function getTypes() {
			return entityTypes || [];
		}

		function structureBuild(structureId) {
			if (structureId) {
				return 'structureNodeId=' + structureId;
			}
		}

		function searchBuild() {
			const currentSearch = searchService.currentSearch();
			let keyword = '';

			if (currentSearch) {
				keyword = currentSearch.keyword || '';
				filters = currentSearch.filters || filters;

				if (currentSearch.context) {
					const defaultFilter = currentSearch.context.defaultQueryFilter;

					if (defaultFilter && filters.indexOf(defaultFilter) !== -1) {
						filters.push(defaultFilter);
					}
				}
			}
			return keyword ? 'search=' + keyword : '';
		}

		function quickFiltersBuild(options, filters) {
			quickFilters = filters;
			return (
				createQuickFilterUrl('redFilter', options.red, true) +
				createQuickFilterUrl('yellowFilter', options.yellow, !options.red) +
				createQuickFilterUrl('greenFilter', options.green, !(options.red || options.yellow))
			);
		}

		function createQuickFilterUrl(name, id, isFirst) {
			return id ? (isFirst ? '' : '&') + name + '=' + id : '';
		}

		function sortingBuild(sortColumns) {
			if (!Array.isArray(sortColumns) || !sortColumns.length) {
				return '';
			}
			return sortColumns.reduce(function (accumulator, column, index) {
				const direction = column.Sorting === ColumnSortingEnum.Ascending ? 'asc' : 'desc';
				return accumulator + (index > 0 ? ',' : '') + column.Name + ' ' + direction;
			}, 'orderBy=');
		}

		function paginationBuild(pageSize, page) {
			return pageSize > 0 ? 'pageSize=' + pageSize + '&page=' + (page || 0) : '';
		}

		function columnsBuild(columns) {
			return columns && Object.keys(columns).length > 0
				? 'columns=' +
						Object.keys(columns)
							.filter(function (key) {
								return columns[key].IsLoading === true;
							})
							.join(',')
				: '';
		}

		async function getDataCount(parts) {
			let dataRequestUrl = widgetId ? 'api/WidgetList/GetData/' + widgetId : 'api/dataquery/' + dataQueryId;

			dataRequestUrl += '/count';

			const response = await _executeRequest(dataRequestUrl, parts);
			totalCount = response.data;
			return response.data;
		}

		function supportsGrouping() {
			return true;
		}

		async function getGroupingTree(columns) {
			const columnCondition =
				'byColumns=' +
				columns
					.map(function (c) {
						return c.Name;
					})
					.join(',');

			const structureParam = structureBuild(structureNodeId);
			const baseUrl = 'api/WidgetList/GroupData/' + widgetId + '/';

			const { data: groupingTree } = await _executeRequest(baseUrl, [columnCondition, structureParam, searchBuild()]);

			if (Array.isArray(groupingTree)) {
				groupingTree.forEach((group) => setGroupsParent(group));
			}

			return groupingTree;
		}

		function setGroupsParent(group) {
			if (typeof group.Value === 'undefined') {
				group.Value = null;
			}
			if (Array.isArray(group.SubGroups)) {
				group.SubGroups.forEach((subGroup) => {
					subGroup.Parent = group;
					setGroupsParent(subGroup);
				});
			}
		}

		function setTotalCount(count) {
			totalCount = Number(count);
		}

		function _generateQueryString(parts) {
			let queryString = '';

			parts.forEach(function (part) {
				queryString = addUrlParam(queryString, part);
			});

			const commonFilters = filters && filters[0] ? filters.concat(quickFilters) : quickFilters;
			if (commonFilters.length) {
				queryString = addUrlParam(queryString, 'filters=' + commonFilters.join());
			}

			return queryString.replace(/&&/gi, '&');

			function addUrlParam(queryString, paramString) {
				return !paramString ? queryString : (queryString ? queryString + '&' : '?') + paramString;
			}
		}

		async function _executeRequest(dataRequestUrl, parts, progressHandler) {
			const filter = searchService.currentSearch().searchForm.toJSON();
			filter.Conditions = filter.Conditions.filter(
				(c) => c.Value.length > 0 || c.Operator === filterOperatorsFactory.operators.IsSet.value || c.Operator === filterOperatorsFactory.operators.IsNotSet.value
			);

			dataRequestUrl += _generateQueryString(parts);
			const options = {};
			if (typeof progressHandler === 'function') {
				options.eventHandlers = {
					progress: progressHandler,
				};
			}

			return http.post(dataRequestUrl, filter, options, {}, false, true);
		}

		async function run(parts, progressHandler) {
			const dataRequestUrl = widgetId ? `api/widgetList/getData/${widgetId}` : `api/dataQuery/${dataQueryId}`;

			const response = await _executeRequest(dataRequestUrl, parts, progressHandler);

			const data = response.data.map(function (item) {
				return objectMapper.mapItem(item);
			});

			totalCount = parseInt(response.headers().totalcount || data.length);

			return {
				data: data,
				totalCount: totalCount,
			};
		}

		function executeExportData() {
			const currentSearch = searchService.currentSearch();
			const exportRequest = {};

			exportRequest.Columns = currentSearch.dataQueryList.columns
				.filter((column) => column.visible)
				.map((column) => column.Name)
				.join(',');

			exportRequest.ExportAllRows = true;
			exportRequest.ExportFormatType = 3; // Excel
			exportRequest.ClientTimeOffset = new Date().getTimezoneOffset();
			exportRequest.DatasetViewId = currentSearch.context.parameters.contentWidgetId || currentSearch.context.widgetId;
			exportRequest.Keyword = currentSearch.keyword;
			exportRequest.ActiveFilter = currentSearch.searchForm.toJSON();

			if (currentSearch.dataQueryList) {
				exportRequest.Filters =
					currentSearch.filters && currentSearch.filters[0]
						? currentSearch.filters.concat(currentSearch.dataQueryList.quickFilterPreprocessor.filters)
						: currentSearch.dataQueryList.quickFilterPreprocessor.filters;

				exportRequest.OrderBy = currentSearch.dataQueryList.sortingPreprocessor.build();
				if (exportRequest.OrderBy) {
					exportRequest.OrderBy = exportRequest.OrderBy.replace('orderBy=', '');
				}
				exportRequest.StructureNodeId = currentSearch.dataQueryList.structurePreprocessor.getNodeId();
			}

			const headers = { responseType: 'arraybuffer' };
			return http.post('api/DataObject/Export', exportRequest, headers, {}, false, true).then(function (response) {
				downloadHelpService.downloadContent(response, response.data);
			});
		}

		function getIdentifier() {
			return 'Id';
		}
	};
}
